import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Button, Card, EmptyState, Spinner } from '../components/ui';
import { brl, dataBr, mesAno, mili, parseMili, parseReais } from '../lib/format';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
interface Unidade {
  id: number;
  numero: string;
  ativo: boolean;
}
interface GasMes {
  id: number;
  dataLeitura: string | null;
  valorTotal: number;
  /** Nulos enquanto o rateio não foi calculado (ou ficou desatualizado por uma correção). */
  valorM3: number | null;
  consumoTotalMili: number | null;
  totalRateado: number | null;
}
interface Leitura {
  id: number;
  unidadeId: number;
  numeroRelogio: string | null;
  leituraAnteriorMili: number;
  leituraAtualMili: number;
  consumoMili: number;
  valor: number | null;
  fotoDocumentoId: number | null;
}
interface Gas {
  gasMes: GasMes | null;
  leituras: Leitura[];
  /** Última leitura atual de cada unidade em competências anteriores. */
  referencias: Array<{ unidadeId: number; leituraAtualMili: number }>;
}

type Base = { comps: Competencia[]; unidades: Unidade[] };
type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; gas: Gas };
type Aviso = { kind: 'success' | 'danger' | 'info'; texto: string };
type CampoMes = 'data' | 'valor';
type CampoLeitura = 'anterior' | 'atual';

const MSG_DATA = 'Informe a data da leitura.';
const MSG_VALOR = 'Informe o valor da conta em reais, como 1.234,56.';
const MSG_LEITURA = 'Informe a leitura em m³, como 1234,567.';
const MSG_MENOR = 'A leitura atual não pode ser menor que a anterior.';
const MSG_FOTO_TIPO = 'Envie uma imagem JPEG, PNG, WebP ou HEIC.';
const MSG_FOTO_TAMANHO = 'A foto passa de 25 MB.';
const FOTO_MAX_BYTES = 25 * 1024 * 1024;
const FOTO_TIPOS: Record<string, true> = {
  'image/jpeg': true,
  'image/png': true,
  'image/webp': true,
  'image/heic': true,
};

/** Erros de validação da API que pertencem a um campo do formulário do mês. */
const ERRO_DE_CAMPO_MES: Record<string, { campo: CampoMes; mensagem: string }> = {
  invalid_valor: { campo: 'valor', mensagem: MSG_VALOR },
  invalid_data: { campo: 'data', mensagem: MSG_DATA },
};
/** Idem para o formulário de leitura. */
const ERRO_DE_CAMPO_LEITURA: Record<string, { campo: CampoLeitura; mensagem: string }> = {
  invalid_leitura: { campo: 'atual', mensagem: MSG_LEITURA },
  leitura_atual_menor: { campo: 'atual', mensagem: MSG_MENOR },
};
/** Demais erros da API (código → mensagem), mostrados acima da tabela. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  gas_mes_duplicado: 'Este mês já tem gás lançado.',
  leitura_duplicada: 'Esta unidade já tem leitura neste mês.',
  gas_mes_not_found: 'Mês de gás não encontrado.',
  gas_leitura_not_found: 'Leitura não encontrada.',
  invalid_mime_foto: MSG_FOTO_TIPO,
  file_too_large: MSG_FOTO_TAMANHO,
  missing_file: 'Escolha uma foto.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível concluir. Tente novamente.';
/** Erros que indicam dado já mudado no servidor: a tela recarrega. */
const ERRO_DE_DADO_VELHO: Record<string, true> = {
  gas_mes_duplicado: true,
  leitura_duplicada: true,
  gas_mes_not_found: true,
  gas_leitura_not_found: true,
};

/**
 * Gás do mês (RF-A07): conta, leitura por unidade com a anterior sugerida, foto do medidor
 * e rateio. `editavel` só para o síndico de condomínio ativo: as escritas são
 * `requireRole('sindico')`, o contábil é somente leitura e o tenant suspenso também (#127).
 * Consumo, valor do m³ e rateio vêm sempre do servidor (`@econdominio/calc`, via
 * `calcular`): a tela recarrega depois de cada alteração e nunca calcula valores.
 */
export function Gas({ editavel }: { editavel: boolean }) {
  const [base, setBase] = useState<Base | 'erro' | null>(null);
  const [sel, setSel] = useState('');
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  // Só o id da unidade: a leitura em edição vem sempre da lista atual.
  const [leituraDe, setLeituraDe] = useState<number | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  // Leitura salva nesta sessão depois do último cálculo: o aviso explica por que sumiu o rateio.
  const [alterada, setAlterada] = useState(false);
  // Competência selecionada no momento: respostas de outra (troca rápida) são descartadas.
  const selAtual = useRef(sel);
  selAtual.current = sel;

  useEffect(() => {
    void Promise.all([
      getJson<{ competencias: Competencia[] }>('/api/competencias'),
      getJson<{ unidades: Unidade[] }>('/api/unidades'),
    ]).then(([cs, us]) => {
      if (!cs || !us) {
        setBase('erro');
        return;
      }
      setBase({ comps: cs.competencias, unidades: us.unidades });
      if (cs.competencias[0]) setSel(String(cs.competencias[0].id));
    });
  }, []);

  const carregar = useCallback(async (competencia: string) => {
    const gas = await getJson<Gas>(`/api/competencias/${encodeURIComponent(competencia)}/gas`);
    if (selAtual.current !== competencia) return;
    setCarga(gas ? { estado: 'ok', gas } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    if (!sel) return;
    setCarga({ estado: 'carregando' });
    setLeituraDe(null);
    setAlterada(false);
    void carregar(sel);
  }, [sel, carregar]);

  /** Erro da API fora dos campos; dado já mudado no servidor fecha o formulário e recarrega. */
  async function tratarFalha(codigo?: string) {
    if (selAtual.current !== sel) return;
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
    if (!codigo || !Object.hasOwn(ERRO_DE_DADO_VELHO, codigo)) return;
    setLeituraDe(null);
    await carregar(sel);
  }

  async function aoSalvarMes() {
    if (selAtual.current !== sel) return;
    setAviso({ kind: 'success', texto: 'Mês de gás registrado.' });
    await carregar(sel);
  }

  async function aoSalvarLeitura() {
    if (selAtual.current !== sel) return;
    setAviso({ kind: 'success', texto: 'Leitura salva.' });
    setAlterada(true);
    setLeituraDe(null);
    await carregar(sel);
  }

  async function calcular(gasMesId: number) {
    if (calculando) return;
    setCalculando(true);
    setAviso(null);
    const res = await mutateJson<{ error?: string }>(
      `/api/gas/${encodeURIComponent(gasMesId)}/calcular`,
      {},
    );
    setCalculando(false);
    if (selAtual.current !== sel) return;
    if (!res.ok) {
      await tratarFalha(res.data?.error);
      return;
    }
    setAlterada(false);
    setAviso({ kind: 'success', texto: 'Rateio calculado.' });
    await carregar(sel);
  }

  async function enviarFoto(leituraId: number, arquivo: File) {
    if (!Object.hasOwn(FOTO_TIPOS, arquivo.type.toLowerCase())) {
      setAviso({ kind: 'danger', texto: MSG_FOTO_TIPO });
      return;
    }
    if (arquivo.size > FOTO_MAX_BYTES) {
      setAviso({ kind: 'danger', texto: MSG_FOTO_TAMANHO });
      return;
    }
    const form = new FormData();
    form.set('file', arquivo);
    setEnviandoFoto(true);
    setAviso(null);
    const res = await mutateJson<{ error?: string }>(
      `/api/gas-leituras/${encodeURIComponent(leituraId)}/foto`,
      form,
    );
    setEnviandoFoto(false);
    if (selAtual.current !== sel) return;
    if (!res.ok) {
      await tratarFalha(res.data?.error);
      return;
    }
    setAviso({ kind: 'success', texto: 'Foto anexada.' });
    await carregar(sel);
  }

  if (base === null) return <Spinner />;
  if (base === 'erro') return <Alert kind="danger">Não foi possível carregar o gás do mês.</Alert>;
  if (base.comps.length === 0) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  const gas = carga.estado === 'ok' ? carga.gas : null;
  const gasMes = gas?.gasMes ?? null;
  const leituraDaUnidade = (unidadeId: number) =>
    gas?.leituras.find((l) => l.unidadeId === unidadeId) ?? null;
  const unidadesDaTabela = base.unidades.filter((u) => u.ativo || leituraDaUnidade(u.id));
  const emEdicao = unidadesDaTabela.find((u) => u.id === leituraDe) ?? null;

  return (
    <div className={styles.stack}>
      <Field label="Competência">
        {(id) => (
          <Select
            id={id}
            value={sel}
            onChange={(e) => {
              setAviso(null);
              setSel(e.target.value);
            }}
            style={{ maxWidth: '12rem' }}
          >
            {base.comps.map((c) => (
              <option key={c.id} value={c.id}>
                {mesAno(c)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}
      {carga.estado === 'carregando' ? <Spinner /> : null}
      {carga.estado === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar o gás do mês.</Alert>
      ) : null}

      {gas && !gasMes ? (
        editavel ? (
          <MesForm competencia={sel} onSalvo={aoSalvarMes} onErroGeral={tratarFalha} />
        ) : (
          <EmptyState>Gás ainda não lançado neste mês.</EmptyState>
        )
      ) : null}

      {gas && gasMes ? (
        <>
          <Card title="Conta do mês">
            <p>
              Conta: {brl(gasMes.valorTotal)}
              {gasMes.dataLeitura ? ` · leitura em ${dataBr(gasMes.dataLeitura)}` : ''}
            </p>
            {gasMes.valorM3 !== null &&
            gasMes.consumoTotalMili !== null &&
            gasMes.totalRateado !== null ? (
              <>
                <p>Consumo total: {mili(gasMes.consumoTotalMili)} m³</p>
                <p>Valor do m³: {brl(gasMes.valorM3)}</p>
                <p>Total rateado: {brl(gasMes.totalRateado)}</p>
              </>
            ) : gas.leituras.length > 0 ? (
              <p>
                {alterada
                  ? 'Leituras alteradas: calcule o rateio novamente.'
                  : 'Rateio ainda não calculado.'}
              </p>
            ) : null}
            {editavel && gas.leituras.length > 0 ? (
              <Button disabled={calculando} onClick={() => void calcular(gasMes.id)}>
                Calcular rateio
              </Button>
            ) : null}
          </Card>

          {editavel && emEdicao ? (
            <LeituraForm
              key={emEdicao.id}
              gasMesId={gasMes.id}
              unidade={emEdicao}
              leitura={leituraDaUnidade(emEdicao.id)}
              referencia={
                gas.referencias.find((r) => r.unidadeId === emEdicao.id)?.leituraAtualMili ?? null
              }
              onSalvo={aoSalvarLeitura}
              onCancelar={() => setLeituraDe(null)}
              bloqueado={calculando}
              onErroGeral={tratarFalha}
            />
          ) : null}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Unidade</th>
                  <th>Relógio</th>
                  <th className={styles.num}>Anterior (m³)</th>
                  <th className={styles.num}>Atual (m³)</th>
                  <th className={styles.num}>Consumo (m³)</th>
                  <th className={styles.num}>Valor</th>
                  <th>Foto</th>
                  {editavel ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {unidadesDaTabela.map((u) => {
                  const l = leituraDaUnidade(u.id);
                  return (
                    <tr key={u.id}>
                      <th scope="row">{u.numero}</th>
                      <td>{l?.numeroRelogio ?? '—'}</td>
                      <td className={styles.num}>{l ? mili(l.leituraAnteriorMili) : '—'}</td>
                      <td className={styles.num}>{l ? mili(l.leituraAtualMili) : '—'}</td>
                      <td className={styles.num}>{l ? mili(l.consumoMili) : '—'}</td>
                      <td className={styles.num}>{l?.valor != null ? brl(l.valor) : '—'}</td>
                      <td>
                        {l?.fotoDocumentoId ? (
                          <a
                            href={`/api/documentos/${l.fotoDocumentoId}/download`}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Ver foto da unidade ${u.numero}`}
                          >
                            Ver foto
                          </a>
                        ) : l ? (
                          'Sem foto'
                        ) : (
                          '—'
                        )}
                      </td>
                      {editavel ? (
                        <td>
                          <div className={styles.row}>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={calculando}
                              aria-label={`${l ? 'Corrigir' : 'Lançar'} leitura da unidade ${u.numero}`}
                              onClick={() => {
                                setAviso(null);
                                setLeituraDe(u.id);
                              }}
                            >
                              {l ? 'Corrigir' : 'Lançar'}
                            </Button>
                            {l ? (
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/heic"
                                aria-label={`Foto do medidor da unidade ${u.numero}`}
                                disabled={enviandoFoto || calculando}
                                onChange={(e) => {
                                  const arquivo = e.target.files?.[0];
                                  e.target.value = '';
                                  if (arquivo) void enviarFoto(l.id, arquivo);
                                }}
                              />
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Registro do mês de gás: data da leitura e valor total da conta. */
function MesForm({
  competencia,
  onSalvo,
  onErroGeral,
}: {
  competencia: string;
  onSalvo: () => Promise<void>;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [data, setData] = useState('');
  const [valor, setValor] = useState('');
  const [erros, setErros] = useState<Partial<Record<CampoMes, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const centavos = parseReais(valor);
    const locais: Partial<Record<CampoMes, string>> = {};
    if (!data) locais.data = MSG_DATA;
    if (centavos === null) locais.valor = MSG_VALOR;
    setErros(locais);
    if (centavos === null || locais.data) return;

    setEnviando(true);
    const res = await mutateJson<{ error?: string }>(
      `/api/competencias/${encodeURIComponent(competencia)}/gas`,
      { dataLeitura: data, valorTotal: centavos },
    );
    setEnviando(false);
    if (res.ok) {
      await onSalvo();
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO_MES[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title="Conta de gás do mês">
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Data da leitura" error={erros.data}>
            {(id) => (
              <Input id={id} type="date" value={data} onChange={(e) => setData(e.target.value)} />
            )}
          </Field>
          <Field label="Valor total da conta" error={erros.valor}>
            {(id) => (
              <Input
                id={id}
                value={valor}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setValor(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
        </div>
        <Button type="submit" disabled={enviando}>
          Registrar mês
        </Button>
      </form>
    </Card>
  );
}

/** Lançamento (`leitura` nula) ou correção da leitura de uma unidade. */
function LeituraForm({
  gasMesId,
  unidade,
  leitura,
  referencia,
  onSalvo,
  onCancelar,
  bloqueado,
  onErroGeral,
}: {
  gasMesId: number;
  unidade: Unidade;
  leitura: Leitura | null;
  /** Leitura atual do mês anterior (milésimos de m³); `null` quando não há. */
  referencia: number | null;
  onSalvo: () => Promise<void>;
  onCancelar: () => void;
  /** Cálculo em andamento: salvar agora faria o rateio ler leituras que acabam de mudar. */
  bloqueado: boolean;
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [relogio, setRelogio] = useState(leitura?.numeroRelogio ?? '');
  const [anterior, setAnterior] = useState(() => {
    if (leitura) return mili(leitura.leituraAnteriorMili);
    return referencia === null ? '' : mili(referencia);
  });
  const [atual, setAtual] = useState(leitura ? mili(leitura.leituraAtualMili) : '');
  const [erros, setErros] = useState<Partial<Record<CampoLeitura, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const ant = parseMili(anterior);
    const atu = parseMili(atual);
    const locais: Partial<Record<CampoLeitura, string>> = {};
    if (ant === null) locais.anterior = MSG_LEITURA;
    if (atu === null) locais.atual = MSG_LEITURA;
    setErros(locais);
    if (ant === null || atu === null) return;

    setEnviando(true);
    const numeroRelogio = relogio.trim();
    const res = leitura
      ? await mutateJson<{ error?: string }>(
          `/api/gas-leituras/${encodeURIComponent(leitura.id)}`,
          { numeroRelogio, leituraAnteriorMili: ant, leituraAtualMili: atu },
          'PATCH',
        )
      : await mutateJson<{ error?: string }>(`/api/gas/${encodeURIComponent(gasMesId)}/leituras`, {
          unidadeId: unidade.id,
          numeroRelogio,
          leituraAnteriorMili: ant,
          leituraAtualMili: atu,
        });
    setEnviando(false);
    if (res.ok) {
      await onSalvo();
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO_LEITURA[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title={`${leitura ? 'Corrigir' : 'Lançar'} leitura da unidade ${unidade.numero}`}>
      <form onSubmit={enviar} noValidate>
        {referencia !== null ? (
          <p className="muted">Leitura do mês anterior: {mili(referencia)} m³</p>
        ) : null}
        <div className={styles.row}>
          <Field label="Relógio">
            {(id) => (
              <Input
                id={id}
                value={relogio}
                maxLength={40}
                onChange={(e) => setRelogio(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
          <Field label="Leitura anterior (m³)" error={erros.anterior}>
            {(id) => (
              <Input
                id={id}
                value={anterior}
                inputMode="decimal"
                placeholder="0,000"
                onChange={(e) => setAnterior(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
          <Field label="Leitura atual (m³)" error={erros.atual}>
            {(id) => (
              <Input
                id={id}
                value={atual}
                inputMode="decimal"
                placeholder="0,000"
                onChange={(e) => setAtual(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Button type="submit" disabled={enviando || bloqueado}>
            Salvar leitura
          </Button>
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
